import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import { toast } from 'react-hot-toast';
import { FiX } from 'react-icons/fi';
import apiService from '../services/apiService';
import reputationService from '../services/reputationService';
import AICorrectionsTab from '../components/admin/AICorrectionsTab';
import AITaskBoardTab from '../components/admin/AITaskBoardTab';
import KatifiWebStudioTab from '../components/admin/KatifiWebStudioTab';
import KatifiMemoryPanel from '../components/admin/KatifiMemoryPanel';
import KatifiVideoArchive from '../components/brain/KatifiVideoArchive';
import { useAuth } from '../context/AuthContext';
import Vapi from '@vapi-ai/web';

// Initialize Vapi SDK conditionally (Fallback)
let vapiInstance = null;
try {
  vapiInstance = new Vapi(process.env.REACT_APP_VAPI_PUBLIC_KEY || 'unconfigured_vapi');
} catch (e) {
  console.warn("Vapi SDK initialization failed: ", e);
}

const AdminBrainCenter = () => {
    const { user } = useAuth();
    
    // Check URL params for active tab
    const urlParams = new URLSearchParams(window.location.search);
    const initialTab = urlParams.get('tab') || 'katifi';
    
    // Evaluate if the URL tab is actually a sub-step of 'creator'
    const isCreatorSubStep = initialTab === 'web-studio' || initialTab === 'architect';
    const parsedTab = isCreatorSubStep ? 'creator' : initialTab;
    
    const [activeTab, setActiveTab] = useState(parsedTab); // 'katifi' | 'brain' | 'phoenix' | 'warfare' | 'creator' | 'cleanup'
    const [creatorStep, setCreatorStep] = useState(initialTab === 'web-studio' ? 'web-studio' : 'architect');

    // Evaluate correctly based on React Context natively
    const isRealCreator = React.useMemo(() => {
        return Boolean(user?.email && user.email.toLowerCase() === 'afitbk@gmail.com');
    }, [user]);

    const isCreator = React.useMemo(() => {
        if (!user) return false;
        if (isRealCreator) return true;
        const userRoles = Array.isArray(user.roles) ? user.roles : [];
        const roleNames = userRoles.map(r => typeof r === 'string' ? r : (r.name || '').toUpperCase());
        return roleNames.includes('CREATOR');
    }, [user, isRealCreator]);

    // Helper function to verify permissions against allowed_tabs map
    const isAllowed = React.useCallback((key) => {
        if (!user) return false;
        
        // Secret Video Archive is strictly for the Creator (afitbk@gmail.com) only
        if (key === 'brain_videoarchive') {
            return isRealCreator;
        }

        if (user.allowed_tabs && user.allowed_tabs[key] !== undefined) {
            return user.allowed_tabs[key];
        }
        const userRoles = Array.isArray(user.roles) ? user.roles : [];
        const roleNames = userRoles.map(r => typeof r === 'string' ? r : (r.name || '').toUpperCase());
        
        // Creator always has access to all sub-tabs by default
        if (roleNames.includes('CREATOR')) return true;

        // Non-creators do NOT get sub-tabs by default (must be explicitly enabled in allowed_tabs)
        if (key.startsWith('brain_') && key !== 'brain_center') {
            return false;
        }

        if (roleNames.includes('SUPERADMIN') || roleNames.includes('MANAGING_PARTNER')) {
            return true;
        }
        return false;
    }, [user, isRealCreator]);

    // Sub-tab definitions mapping keys to specific permission keys
    const TABS_CONFIG = React.useMemo(() => [
        { key: 'creator', label: 'Create or Re-design', permission: 'brain_creator', borderColor: '#14b8a6', activeColor: '#0f766e', icon: '✨' },
        { key: 'katifi', label: isCreator ? 'Katifi AI' : 'Julifi AI', permission: 'brain_katifi', borderColor: '#6366f1', activeColor: '#4f46e5', icon: '🧿' },
        { key: 'brain', label: 'Neural Wiring', permission: 'brain_neural_wiring', borderColor: '#7c3aed', activeColor: '#7c3aed', icon: '🧠' },
        { key: 'phoenix', label: 'Operation Phoenix', permission: 'brain_phoenix', borderColor: '#f59e0b', activeColor: '#d97706', icon: '🦅' },
        { key: 'warfare', label: 'Content Warfare', permission: 'brain_warfare', borderColor: '#ef4444', activeColor: '#b91c1c', icon: '📜' },
        { key: 'dmx62', label: 'Reality Override', permission: 'brain_reality_override', borderColor: '#8b5cf6', activeColor: '#8b5cf6', icon: '🔮' },
        { key: 'corrections', label: 'AI Corrections', permission: 'brain_corrections', borderColor: '#10b981', activeColor: '#059669', icon: '🛡️' },
        { key: 'virtual_office', label: 'The Virtual Office', permission: 'brain_virtual_office', icon: '🏢', external: true, path: '/ai-staff' },
        { key: 'memory', label: 'Memory Panel', permission: 'brain_memory', borderColor: '#6366f1', activeColor: '#4f46e5', icon: '🧠' },
        { key: 'cleanup', label: 'Site Cleanup', permission: 'brain_cleanup', borderColor: '#14b8a6', activeColor: '#0f766e', icon: '🧹' },
        { key: 'taskboard', label: 'Task Board', permission: 'brain_taskboard', borderColor: '#ec4899', activeColor: '#be185d', icon: '📋' },
        { key: 'videoarchive', label: 'Видео Архив', permission: 'brain_videoarchive', icon: '🔐', creatorOnly: true, isSpecial: true }
    ], [isCreator]);

    // Filter allowed tabs based on roles and permitted keys
    const allowedSubTabs = React.useMemo(() => {
        return TABS_CONFIG.filter(tab => {
            if (tab.key === 'videoarchive' || tab.permission === 'brain_videoarchive') {
                return isRealCreator;
            }
            if (tab.creatorOnly && !isCreator) return false;
            return isAllowed(tab.permission);
        });
    }, [TABS_CONFIG, isCreator, isRealCreator, isAllowed]);

    // Force tab validation / routing redirects
    useEffect(() => {
        if (!isAllowed('brain_center')) return;

        const isCurrentTabAllowed = allowedSubTabs.some(t => t.key === activeTab);
        if (!isCurrentTabAllowed && allowedSubTabs.length > 0) {
            const firstAllowed = allowedSubTabs.find(t => !t.external) || allowedSubTabs[0];
            setActiveTab(firstAllowed.key);
        }
    }, [allowedSubTabs, activeTab, isAllowed]);

    const [katifiMessages, setKatifiMessages] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);

    useEffect(() => {
        if (!user) return;
        
        let title = "Сотрудник";
        if (user.email === "afitbk@gmail.com") {
            title = "Создатель";
        } else if (isCreator) {
            title = "Директор";
        } else if (user.first_name) {
            title = user.first_name;
        }

        const introMsg = isCreator 
            ? `Здравствуйте, ${title}. Я Катифи — ваш главный системный ИИ-менеджер. Рой работает в штатном режиме. Чем могу помочь сегодня?`
            : `Здравствуйте, ${title}. Я Джулифи — ваш личный профессиональный помощник. Чем могу помочь сегодня?`;

        setKatifiMessages([
            { role: 'model', content: introMsg }
        ]);
    }, [user, isCreator]);

    const [katifiInput, setKatifiInput] = useState('');
    const [katifiLoading, setKatifiLoading] = useState(false);
    
    // [NEW] Vision Attachment State
    const [attachmentBase64, setAttachmentBase64] = useState(null);
    const [attachmentMimeType, setAttachmentMimeType] = useState(null);
    
    const katifiChatEndRef = React.useRef(null);
    const katifiInputRef = React.useRef(null);
    const hiddenFileInput = React.useRef(null);

    // [NEW] Voice Call State
    const [isCalling, setIsCalling] = useState(false);
    const [vapiActive, setVapiActive] = useState(false);

    useEffect(() => {
        if (vapiInstance) {
          vapiInstance.on('call-start', () => {
            setIsCalling(true);
            setVapiActive(true);
            setKatifiMessages(prev => [...prev, { role: 'system', content: '📞 Fallback VAPI Voice Engine Active.' }]);
          });

          vapiInstance.on('call-end', () => {
            setIsCalling(false);
            setVapiActive(false);
            setKatifiMessages(prev => [...prev, { role: 'system', content: '📞 Voice connection terminated.' }]);
          });
          
          vapiInstance.on('error', (e) => {
            console.error("Vapi Error", e);
            setIsCalling(false);
            setKatifiMessages(prev => [...prev, { role: 'model', content: `⚠️ Voice Error: ${e.message || 'Unknown error'}` }]);
          });
        }

        return () => {
          if (vapiInstance) vapiInstance.stop();
        };
    }, []);

    const toggleCall = () => {
        if (isCalling) {
          if (vapiInstance && vapiActive) {
             vapiInstance.stop();
          } else {
             // Generic MLG Engine Stop
             setIsCalling(false);
             setKatifiMessages(prev => [...prev, { role: 'system', content: '📞 MLG Voice Engine disconnected.' }]);
          }
        } else {
          // Simulate attempting MLG Voice Engine first, then fallback to VAPI
          setKatifiMessages(prev => [...prev, { role: 'system', content: 'Initiating MLG Voice Engine (Primary)...' }]);
          
          if (process.env.REACT_APP_VAPI_PUBLIC_KEY) {
            setKatifiMessages(prev => [...prev, { role: 'system', content: '⚠️ Warning: Primary MLG WebRTC unavailable. Routing to VAPI Fallback...' }]);
            if (vapiInstance) {
               vapiInstance.start(process.env.REACT_APP_VAPI_ASSISTANT_ID || '');
            }
          } else {
            // Mock connection for purely MLG environments
            setIsCalling(true);
            setKatifiMessages(prev => [...prev, { role: 'system', content: '📞 MLG Voice Engine Handshake Complete. Listening...' }]);
          }
        }
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onloadend = () => {
            setAttachmentBase64(reader.result);
            setAttachmentMimeType(file.type);
        };
        reader.readAsDataURL(file);
    };

    useEffect(() => {
        if (katifiChatEndRef.current) {
            katifiChatEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
        if (activeTab === 'katifi' && katifiInputRef.current && !katifiLoading) {
            setTimeout(() => {
                if (katifiInputRef.current) katifiInputRef.current.focus();
            }, 50);
        }
    }, [katifiMessages, katifiLoading, activeTab]);

    // Brain State
    const [promptKey, setPromptKey] = useState('content_factory_master');
    const [content, setContent] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Phoenix State
    const [phoenixYear, setPhoenixYear] = useState(2019);
    const [phoenixMonth, setPhoenixMonth] = useState('');
    const [phoenixLimit, setPhoenixLimit] = useState(5);
    const [phoenixDelay, setPhoenixDelay] = useState(0); // [NEW] Speed Control
    const [remastering, setRemastering] = useState(false);
    const [phoenixLog, setPhoenixLog] = useState([]);

    // [NEW] History & Diff
    const [history, setHistory] = useState([]); // Phoenix History
    const [warfareHistory, setWarfareHistory] = useState([]); // Global Strategy Tasks
    const [selectedDiff, setSelectedDiff] = useState(null);

    // [NEW] Site Architect (Website Factory) State
    const [archUrl, setArchUrl] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get('domain') || '';
    });
    const [archCity, setArchCity] = useState('');
    const [archPlaceId, setArchPlaceId] = useState('');
    const [archColors, setArchColors] = useState('');
    const [archPrompt, setArchPrompt] = useState('');
    const [archPages, setArchPages] = useState({ home: true, about: true, practice: true, blog: true, contact: true, analytics: true });
    const [archElements, setArchElements] = useState({ chat: true, calendar: true, header: false });
    const [archTerminalOpen, setArchTerminalOpen] = useState(false);
    const [archLogs, setArchLogs] = useState([]);
    const [archSessionId] = useState(() => 'arch_' + Math.random().toString(36).substring(2, 9));
    const [archDomains, setArchDomains] = useState([]);

    // [NEW] Analyst / Architect State
    const [analyzing, setAnalyzing] = useState(false);
    const [proposedPlan, setProposedPlan] = useState(null);
    const [executingPlan, setExecutingPlan] = useState(false);

    useEffect(() => {
        fetchPrompt();
    }, [promptKey]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    // [NEW] Poll History when tab is active
    useEffect(() => {
        let interval;
        if (activeTab === 'phoenix') {
            fetchHistory();
            interval = setInterval(fetchHistory, 5000);
        } else if (activeTab === 'warfare') {
            fetchWarfareHistory();
            interval = setInterval(fetchWarfareHistory, 10000); // 10s poll for global tasks
        } else if (activeTab === 'cleanup') {
            fetchCleanupLogs();
            interval = setInterval(fetchCleanupLogs, 5000);
        } else if (activeTab === 'architect') {
            fetchArchDomains();
        } else if (activeTab === 'dmx62') {
            fetchOllamaStatus();
        }
        return () => clearInterval(interval);
    }, [activeTab]);

    const fetchArchDomains = async () => {
        try {
            const data = await apiService.makeRequest('/organic-seo/domains');
            const domainList = Array.isArray(data) ? data : (data?.domains || []);
            setArchDomains(domainList);
            
            const urlParams = new URLSearchParams(window.location.search);
            const urlDomainId = urlParams.get('domain'); 
            
            if (urlDomainId) {
                const cleanUrlParam = urlDomainId.replace(/^https?:\/\//, '').replace(/\/$/, '');
                const exactMatch = domainList.find(d => {
                    const dStr = typeof d === 'string' ? d : (d.domain || d.url || '');
                    return dStr.includes(cleanUrlParam) || cleanUrlParam.includes(dStr);
                });
                
                if (exactMatch) {
                    setArchUrl(typeof exactMatch === 'string' ? exactMatch : (exactMatch.domain || exactMatch.url));
                } else {
                    setArchUrl(cleanUrlParam);
                }
            } else if (domainList.length > 0 && !archUrl) {
                const first = domainList[0];
                setArchUrl(typeof first === 'string' ? first : (first.domain || first.url));
            }
        } catch (e) {
            console.log("Arch Domains fetch error", e);
        }
    };

    const fetchCleanupLogs = async () => {
        try {
            const data = await apiService.makeRequest('/organic-seo/remove-lawyer/logs');
            setCleanupLogs(data);
        } catch (e) {
            console.log("Cleanup Logs poll error", e);
        }
    };

    const fetchHistory = async () => {
        try {
            // Direct API call since reputationService might not have this method yet exposed simply
            const data = await apiService.makeRequest('/reputation/phoenix/history');
            setHistory(data);
        } catch (e) {
            // Silent fail for polling
            console.log("History poll", e);
        }
    };

    // [NEW] DMX62 State
    const [dmxKeyword, setDmxKeyword] = useState('');
    const [dmxUrl, setDmxUrl] = useState('');
    const [dmxStatus, setDmxStatus] = useState(null);
    const [dmxLoading, setDmxLoading] = useState(false);

    // [NEW] Ollama Local AI Shield State
    const [ollamaStatus, setOllamaStatus] = useState(null);
    const [ollamaLoading, setOllamaLoading] = useState(false);

    const fetchOllamaStatus = async () => {
        try {
            const data = await apiService.makeRequest('/reputation/dmx62/ollama-status');
            setOllamaStatus(data);
        } catch (e) {
            console.log("Ollama status check failed:", e);
        }
    };

    const handleRunOllamaAudit = async () => {
        setOllamaLoading(true);
        try {
            const response = await apiService.makeRequest('/reputation/dmx62/ollama-audit', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    query: dmxKeyword || 'Morgan Legal Group Reviews Brooklyn',
                    suspect_url: dmxUrl || 'https://www.yelp.com/biz/morgan-martinez-legal-group-brooklyn',
                    canonical_brand: 'Morgan Legal Group P.C.'
                })
            });
            setDmxStatus({
                worker: 'Harvey Yelp (DeepSeek-R1)',
                status: 'ollama_audit_ready',
                ...response
            });
            toast.success("🤖 DeepSeek-R1 Local Audit Complete (0$ Cost)!");
        } catch (e) {
            console.error("Ollama audit failed:", e);
            toast.error("Ollama Audit Failed: " + (e.message || "Unknown error"));
        } finally {
            setOllamaLoading(false);
        }
    };

    // [NEW] Site Cleanup State
    const [cleanupFirstName, setCleanupFirstName] = useState('');
    const [cleanupLastName, setCleanupLastName] = useState('');
    const [isCleaning, setIsCleaning] = useState(false);
    const [cleanupLogs, setCleanupLogs] = useState([]);
    const [selectedCleanupLog, setSelectedCleanupLog] = useState(null);

    const handleSiteCleanup = async () => {
        if (!cleanupFirstName || !cleanupLastName) {
            toast.error("Please provide both First and Last Name.");
            return;
        }

        if (!window.confirm(`Are you sure you want to search and delete ALL pages mentioning ${cleanupFirstName} ${cleanupLastName} across all connected sites? This action cannot be undone.`)) {
            return;
        }

        setIsCleaning(true);
        try {
            const response = await apiService.makeRequest('/organic-seo/remove-lawyer', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    first_name: cleanupFirstName.trim(),
                    last_name: cleanupLastName.trim()
                })
            });

            toast.success(`Cleanup Initiated! Tasks queued to purge ${cleanupFirstName} ${cleanupLastName}.`);
            setCleanupFirstName('');
            setCleanupLastName('');
            fetchCleanupLogs();
        } catch (e) {
            console.error("Cleanup Failed:", e);
            toast.error("Cleanup Protocol Failed: " + (e.message || "Unknown Error"));
        } finally {
            setIsCleaning(false);
        }
    };

    // [NEW] Site Architect Handler
    const handleArchitectSubmit = async (e) => {
        e.preventDefault();
        
        const pagesList = Object.entries(archPages).filter(([k, v]) => v).map(([k]) => k).join(', ');
        const elementsList = Object.entries(archElements).filter(([k, v]) => v).map(([k]) => k).join(', ');

        const finalCommand = `Build / Redesign Website Brief:
Target URL: ${archUrl}
SEO Location/City Target: ${archCity}
Google Place ID: ${archPlaceId || 'Not Provided'}
Color Palette & Styling: ${archColors}

Pages to Generate: ${pagesList}
Elements to PRESERVE/Inject: ${elementsList}

CORE CONTENT STRATEGY & INSTRUCTIONS:
${archPrompt}`;

        setArchTerminalOpen(true);
        setArchLogs([{ step: 'Initialization', details: `Website Architect launched for ${archUrl || 'New Project'}` }]);
        
        try {
            const response = await fetch('/api/v1/admin/katifi/manus/execute', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ command: finalCommand, session_id: archSessionId })
            });

            if (!response.ok) throw new Error(`Architect Endpoint Error: ${response.status}`);

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let isComplete = false;
            
            while (!isComplete) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = decoder.decode(value, { stream: true });
                const lines = chunk.split('\n');
                
                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        const dataStr = line.replace('data: ', '').trim();
                        if (dataStr === '[DONE]') {
                            isComplete = true;
                            setArchLogs(prev => [...prev, { step: 'Complete', details: 'Website architecture complete. Check your website preview.' }]);
                            break;
                        }
                        try {
                            const parsed = JSON.parse(dataStr);
                            setArchLogs(prev => [...prev, { step: parsed.step, details: parsed.details }]);
                        } catch (err) {
                            console.error("SSE parse error", err);
                        }
                    }
                }
            }
        } catch (error) {
            console.error("Architect Stream Error:", error);
            setArchLogs(prev => [...prev, { step: 'Error', details: error.message }]);
        }
    };

    // [NEW] DMX62 Handler
    const handleDMXOverride = async () => {
        if (!dmxKeyword || !dmxUrl) {
            toast.error("Please provide both Keyword and Fake Source URL");
            return;
        }

        setDmxLoading(true);
        try {
            // Call Backend DMX62 Engine
            const response = await apiService.makeRequest('/reputation/dmx62/override', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    keyword: dmxKeyword,
                    fake_url: dmxUrl,
                    profile_id: 'default' // Should be dynamic in v2
                })
            });

            setDmxStatus(response);
            toast.success(`Reality Override Initiated! Risk Score: ${response.metrics.risk_score}%`);

            if (response.metrics.risk_score > 70) {
                toast("⚠️ High Risk Detected: Semantic Flooding Triggered!", { icon: "☢️" });
            }

        } catch (e) {
            console.error("DMX62 Failed:", e);
            toast.error("Override Protocol Failed: " + (e.message || "Unknown Error"));
        } finally {
            setDmxLoading(false);
        }
    };

    const handleHarveyYelpDossier = async () => {
        setDmxLoading(true);
        try {
            const response = await apiService.makeRequest('/reputation/dmx62/yelp-dossier', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    profile_id: 'default',
                    yelp_url: dmxUrl || 'https://www.yelp.com/biz/morgan-martinez-legal-group-brooklyn',
                    erroneous_name: 'Morgan Martinez Legal Group'
                })
            });
            setDmxStatus(response);
            toast.success("Harvey Yelp Evidence Dossier Generated!");
        } catch (e) {
            console.error("Yelp Dossier Failed:", e);
            toast.error("Failed to generate dossier: " + (e.message || "Unknown Error"));
        } finally {
            setDmxLoading(false);
        }
    };


    const fetchWarfareHistory = async () => {
        try {
            // Fetch Global Strategic Tasks (The backend now supports Admin View)
            const data = await apiService.makeRequest('/reputation/tasks');
            // Data is List[StrategicTaskResponse]
            setWarfareHistory(data);
        } catch (e) {
            console.log("Warfare poll", e);
        }
    };

    const handleBatchExecute = async () => {
        if (!window.confirm("☢️ Launch Content Factory for ALL queued tasks?\n\nThis will trigger the AI Writer for every pending item.")) return;

        try {
            const data = await apiService.makeRequest('/reputation/tasks/execute-batch/all', {
                method: 'POST'
            });

            alert(`🏭 Validated! Factory Started.\n\nProcessing ${data.processed} tasks.\nCheck 'Content Warfare' tab for live updates.`);
            fetchWarfareHistory(); // Refresh

        } catch (e) {
            console.error(e);
            alert("Error connecting to Content Factory.");
        }
    };

    const fetchPrompt = async () => {
        setLoading(true);
        try {
            // Using makeRequest directly for new endpoint
            const data = await apiService.makeRequest(`/admin/prompts/${promptKey}`);
            setContent(data.content);
            setDescription(data.description);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load System Prompt. Is the DB seeded?");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await apiService.makeRequest(`/admin/prompts/${promptKey}`, {
                method: 'PUT',
                body: JSON.stringify({ content, description })
            });
            toast.success("Brain Updated! Next generation will use this.");
        } catch (error) {
            toast.error("Failed to update Brain.");
        } finally {
            setSaving(false);
        }
    };

    const handlePhoenixTrigger = async () => {
        setRemastering(true);
        const monthLabel = phoenixMonth ? new Date(0, phoenixMonth - 1).toLocaleString('default', { month: 'long' }) : 'All Months';

        try {
            toast.loading(`Igniting Phoenix for ${phoenixYear} (${monthLabel})...`, { id: 'phoenix' });

            const result = await reputationService.triggerPhoenixRemaster(
                phoenixYear,
                phoenixMonth || null,
                phoenixLimit,
                phoenixDelay // [NEW]
            );

            toast.success("Phoenix Protocol Initiated!", { id: 'phoenix' });
            setPhoenixLog(prev => [`[${new Date().toLocaleTimeString()}] 🦅 Task Queued: ${result.task_id} (${result.status})`, ...prev]);

        } catch (error) {
            console.error(error);
            toast.error(`Ignition Failed: ${error.message}`, { id: 'phoenix' });
            setPhoenixLog(prev => [`[${new Date().toLocaleTimeString()}] ❌ Error: ${error.message}`, ...prev]);
        } finally {
            setRemastering(false);
        }
    };

    // [NEW] Analyst Actions
    const handleSummonArchitect = async () => {
        setAnalyzing(true);
        setProposedPlan(null);
        toast.loading("The Architect is studying the battlefield...", { id: 'analyst' });
        try {
            // Call the new RPC
            const data = await apiService.makeRequest('/reputation/analyze', {
                method: 'POST',
                body: JSON.stringify({ limit: 50 })
            });

            if (data.status === 'empty') {
                toast.error(data.message, { id: 'analyst' });
            } else {
                setProposedPlan(data.plan);
                toast.success("Battle Plan Proposed!", { id: 'analyst' });
            }
        } catch (e) {
            toast.error("Analysis Failed: " + e.message, { id: 'analyst' });
        } finally {
            setAnalyzing(false);
        }
    };

    const handleExecutePlan = async () => {
        if (!proposedPlan) return;
        setExecutingPlan(true);
        try {
            const data = await apiService.makeRequest('/reputation/campaign/execute', {
                method: 'POST',
                body: JSON.stringify({ plan: proposedPlan })
            });
            toast.success(`Campaign Executed! ${data.tasks_created} Tasks Queued.`);
            setProposedPlan(null); // Clear after success
            fetchWarfareHistory(); // Refresh logs
        } catch (e) {
            toast.error("Execution Failed: " + e.message);
        } finally {
            setExecutingPlan(false);
        }
    };

    // [NEW] Manual Strategy Control
    const [manualKeyword, setManualKeyword] = useState('');
    const [manualPriority, setManualPriority] = useState('normal');
    const [addingManual, setAddingManual] = useState(false);

    const handleManualAdd = async () => {
        const cleanKeyword = manualKeyword.trim();
        if (!cleanKeyword) return;

        setAddingManual(true);
        try {
            // Deduplication Check (Client-side fast check, backend also checks)
            // Simplified: We rely on backend to handle duplicates gracefully

            const payload = {
                query: cleanKeyword, // Backend expects 'query'
                priority: manualPriority,
                agent_type: "SEO_AGENT",
                recipe: { mode: "manual_override" },
                ai_config: { mode: "standard" }
            };

            const response = await apiService.makeRequest('/reputation/tasks', {
                method: 'POST',
                body: JSON.stringify(payload)
            });

            toast.success(`Task Queued: ${response.query}`);
            setManualKeyword(''); // Clear input
            fetchWarfareHistory(); // Refresh table immediately

        } catch (error) {
            console.error("Manual Add Error:", error);
            // User-friendly Error Message
            let msg = error.message || "Failed to add task.";
            if (msg.includes("500") || msg.includes("Internal Server")) {
                msg = "System Error: Please check Database Connection.";
            } else if (msg.includes("404")) {
                msg = "Profile not found. Are you Admin?";
            }
            toast.error(msg);
        } finally {
            setAddingManual(false);
        }
    };

    const handleKatifiSubmit = async (e) => {
        e.preventDefault();
        if (!katifiInput.trim() && !attachmentBase64) return;
        if (katifiLoading) return;

        const newUserMsg = { 
            role: 'user', 
            content: katifiInput.trim() || "(Sent an image)",
            ...(attachmentBase64 && { 
                image_base64: attachmentBase64,
                mime_type: attachmentMimeType 
            })
        };
        const updatedMessages = [...katifiMessages, newUserMsg];
        
        setKatifiMessages(updatedMessages);
        setKatifiInput('');
        setAttachmentBase64(null);
        setAttachmentMimeType(null);
        setKatifiLoading(true);

        try {
            const data = await apiService.makeRequest('/admin/katifi/chat', {
                method: 'POST',
                body: JSON.stringify({ messages: updatedMessages })
            });

            setKatifiMessages([...updatedMessages, { role: 'model', content: data.response }]);
        } catch (err) {
            toast.error("Katifi connection lost: " + err.message);
            setKatifiMessages([...updatedMessages, { role: 'model', content: "⚠️ Warning: Neural link severed. Please check system diagnotics." }]);
        } finally {
            setKatifiLoading(false);
        }
    };

    const renderKatifiMessage = (text) => {
        if (!text) return null;
        // Make exclamation mark Optional and add lookahead for common image extensions so regular links are ignored
        const imgRegex = /!?\[([^\]]*)\]\((.*?(\.jpg|\.png|\.webp|\.gif).*?)\)/g;
        const parts = [];
        let lastIndex = 0;
        let match;
        
        while ((match = imgRegex.exec(text)) !== null) {
            const imgUrl = match[2];
            const imgAlt = match[1];
            const matchIndex = match.index;
            const matchLength = match[0].length;

            if (matchIndex > lastIndex) {
                parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex, matchIndex)}</span>);
            }
            parts.push(
                <img 
                    key={`img-${matchIndex}`} 
                    src={imgUrl} 
                    alt={imgAlt} 
                    style={{ maxWidth: '100%', borderRadius: '8px', marginTop: '10px', marginBottom: '10px', display: 'block', cursor: 'zoom-in' }}
                    onClick={() => setSelectedImage(imgUrl)}
                />
            );
            lastIndex = matchIndex + matchLength;
        }
        
        if (lastIndex < text.length) {
            parts.push(<span key={`text-${lastIndex}`}>{text.substring(lastIndex)}</span>);
        }
        return parts.length > 0 ? parts : text;
    };

    const lightboxPortal = selectedImage
        ? ReactDOM.createPortal(
            <div
                className="katifi-lightbox"
                onClick={() => setSelectedImage(null)}
                style={{ position: 'fixed', inset: 0, zIndex: 999999,
                         background: 'rgba(0,0,0,0.92)',
                         display: 'flex', alignItems: 'center', justifyContent: 'center',
                         flexDirection: 'column', gap: '16px' }}
            >
                <div
                    className="katifi-lightbox-close"
                    style={{ position: 'absolute', top: '20px', right: '24px',
                             cursor: 'pointer', color: '#fff', fontSize: '28px' }}
                >
                    <FiX />
                </div>
                <img
                    src={selectedImage}
                    alt="Enlarged view"
                    className="katifi-lightbox-img"
                    onClick={(e) => e.stopPropagation()}
                    style={{ maxWidth: '90vw', maxHeight: '80vh', borderRadius: '12px',
                             objectFit: 'contain', boxShadow: '0 8px 48px rgba(0,0,0,0.6)' }}
                />
                <a
                    href={selectedImage.startsWith('/api/') ? `${window.location.origin}${selectedImage}` : selectedImage}
                    download
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: '#fff', background: 'rgba(255,255,255,0.15)',
                             padding: '8px 20px', borderRadius: '8px',
                             textDecoration: 'none', fontSize: '14px', backdropFilter: 'blur(4px)' }}
                >
                    ⬇ Скачать
                </a>
            </div>,
            document.body
          )
        : null;

    // Check main Brain Center permission
    if (!isAllowed('brain_center')) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', backgroundColor: 'hsl(var(--background))', padding: '20px' }}>
                <div style={{ textAlign: 'center', background: '#fff', padding: '40px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxWidth: '450px' }}>
                    <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔒</div>
                    <h2 style={{ fontSize: '24px', color: '#0f172a', fontWeight: 'bold', marginBottom: '10px' }}>Доступ ограничен</h2>
                    <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.5', margin: '0 0 20px 0' }}>
                        У вас нет прав для доступа к Brain Center. Пожалуйста, обратитесь к системному администратору.
                    </p>
                </div>
            </div>
        );
    }

    // Check if any sub-tabs are enabled
    // 🔴 ДВА ЗАШИТЫХ ЦВЕТА В ЭТОМ БЛОКЕ ОСТАВЛЕНЫ НАРОЧНО, И ПРИЧИНА НАЗВАНА.
    // Слияние принесло сюда палитру fitenko зашитыми значениями — фон, рамка,
    // заголовок и подложка чипа сведены к триплетам shadcn (они объявлены и в
    // :root, и под html.fitenko, поэтому у fitenko вид тот же, а mlg/eln/elfny
    // получают СВОЙ цвет вместо утёкшего). А два ТЕКСТОВЫХ цвета — нет:
    //   · приглушённый текст абзаца и золотой текст чипа требуют `*-ink`-
    //     вариантов (у --ink-muted 4,34, у --gold 2,00 — оба ниже 4,5);
    //   · `*-ink` живут ТОЛЬКО под рубежом html.fitenko, а у этого экрана НЕТ
    //     своего .css, где можно было бы завести ИМЯ ЭКРАНА с двумя
    //     объявлениями (как --cal-muted-ink в CalendarPage.css).
    // Завести второго держателя или вписать запасное значение внутрь var()
    // запрещено шапкой styles/fitenko-tokens.css. Долг НАЗВАН и оставлен
    // целиком, а не замазан наполовину молча.
    if (allowedSubTabs.length === 0) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', backgroundColor: 'hsl(var(--background))', padding: '24px' }}>
                <div style={{ textAlign: 'center', background: 'hsl(var(--card))', padding: '48px 36px', borderRadius: '20px', border: '1px solid hsl(var(--border))', boxShadow: '0 1px 3px rgba(32, 35, 31, 0.04)', maxWidth: '460px' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🧠</div>
                    <h2 style={{ fontSize: '20px', fontFamily: 'Georgia, serif', color: 'hsl(var(--foreground))', fontWeight: '700', marginBottom: '8px' }}>Нет активных модулей</h2>
                    <p style={{ color: '#687077', fontSize: '13px', lineHeight: '1.6', margin: '0 0 24px 0' }}>
                        Brain Center доступен, но у вас не включен ни один из его подразделов. Обратитесь к управляющему партнеру для расширения доступа.
                    </p>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderRadius: '10px', background: 'hsl(var(--accent))', border: '1px solid hsl(var(--primary) / 0.35)', color: '#A97F2E', fontSize: '11px', fontWeight: '600' }}>
                        <span>🔒 Fitenko Law Security Gate</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container" style={{ padding: '20px', width: '100%', maxWidth: '100%', margin: '0 auto', backgroundColor: 'hsl(var(--background))', minHeight: '100vh' }}>
            {/* Header */}
            <header style={{ marginBottom: '30px', borderBottom: '1px solid #e2e8f0', paddingBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>
                            🧠 AI Brain Center
                        </h1>
                        <p style={{ color: '#64748b', marginTop: '5px' }}>
                            Directly wire the neural pathways of the Content Factory.
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={fetchPrompt}
                            className="btn-secondary"
                            style={{ padding: '10px 20px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer' }}
                        >
                            Reset
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="btn-primary"
                            style={{
                                padding: '10px 20px',
                                borderRadius: '8px',
                                border: 'none',
                                background: saving ? '#94a3b8' : '#7c3aed',
                                color: '#fff',
                                fontWeight: '600',
                                cursor: saving ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {saving ? 'Rewiring...' : 'Save Changes'}
                        </button>
                    </div>
                </div>
            </header>

            {/* TABS HEADER */}
            <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', borderBottom: '2px solid #e2e8f0', overflowX: 'auto', paddingBottom: '5px' }}>
                {allowedSubTabs.map((tab) => {
                    const isActive = activeTab === tab.key;
                    
                    if (tab.external) {
                        return (
                            <button
                                key={tab.key}
                                onClick={() => window.location.href = tab.path}
                                style={{
                                    padding: '10px 20px',
                                    background: 'transparent',
                                    border: 'none',
                                    borderBottom: '3px solid transparent',
                                    fontWeight: '600',
                                    color: '#64748b',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {tab.icon} {tab.label}
                            </button>
                        );
                    }
                    
                    if (tab.isSpecial && tab.key === 'videoarchive') {
                        if (!isRealCreator) return null;
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setActiveTab('videoarchive')}
                                style={{
                                    padding: '10px 20px',
                                    background: isActive ? 'linear-gradient(135deg, #4c1d95, #6d28d9)' : 'transparent',
                                    border: isActive ? '1px solid #7c3aed' : 'none',
                                    borderBottom: isActive ? '3px solid #a78bfa' : '3px solid transparent',
                                    fontWeight: '600',
                                    color: isActive ? '#e0e7ff' : '#64748b',
                                    cursor: 'pointer',
                                    fontSize: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    whiteSpace: 'nowrap',
                                    borderRadius: isActive ? '8px 8px 0 0' : 0
                                }}
                            >
                                {tab.icon} {tab.label}
                            </button>
                        );
                    }
                    
                    return (
                        <button
                            key={tab.key}
                            onClick={() => {
                                if (tab.key === 'creator') {
                                    setActiveTab('creator');
                                    setCreatorStep('architect');
                                } else {
                                    setActiveTab(tab.key);
                                }
                            }}
                            style={{
                                padding: '10px 20px',
                                background: 'transparent',
                                border: 'none',
                                borderBottom: isActive ? `3px solid ${tab.borderColor}` : '3px solid transparent',
                                fontWeight: '600',
                                color: isActive ? tab.activeColor : '#64748b',
                                cursor: 'pointer',
                                fontSize: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {tab.icon} {tab.label}
                        </button>
                    );
                })}
            </div>

            {activeTab === 'katifi' ? (
                <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 220px)', minHeight: '600px', background: '#fff', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflow: 'hidden' }}>
                    {/* Header */}
                    <div style={{ padding: '20px', background: '#e0e7ff', borderBottom: '1px solid #c7d2fe', display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <div style={{ fontSize: '32px' }}>🧿</div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '18px', color: '#3730a3', fontWeight: 'bold' }}>{isCreator ? 'Katifi AI' : 'Julifi AI'}</h2>
                            <p style={{ margin: 0, fontSize: '13px', color: '#4f46e5' }}>{isCreator ? 'Chief AI Systems Manager' : 'Integrated Professional Assistant'}</p>
                        </div>
                    </div>

                    {/* Chat History */}
                    <div style={{ flex: 1, padding: '20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '15px', background: '#f8fafc' }}>
                        {katifiMessages.map((msg, idx) => (
                            <div key={idx} style={{ display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                <div style={{
                                    maxWidth: '75%',
                                    padding: '12px 18px',
                                    borderRadius: '16px',
                                    background: msg.role === 'user' ? '#4f46e5' : (msg.role === 'system' ? '#f1f5f9' : '#fff'),
                                    color: msg.role === 'user' ? '#fff' : (msg.role === 'system' ? '#64748b' : '#1e293b'),
                                    boxShadow: msg.role === 'user' ? '0 4px 6px -1px rgba(79, 70, 229, 0.3)' : '0 2px 4px rgba(0,0,0,0.05)',
                                    border: (msg.role === 'model' || msg.role === 'system') ? '1px solid #e2e8f0' : 'none',
                                    borderTopRightRadius: msg.role === 'user' ? '4px' : '16px',
                                    borderTopLeftRadius: (msg.role === 'model' || msg.role === 'system') ? '4px' : '16px',
                                    fontFamily: msg.role === 'system' ? 'monospace' : 'inherit',
                                    fontSize: msg.role === 'system' ? '13px' : '15px',
                                    lineHeight: '1.5',
                                    whiteSpace: 'pre-wrap'
                                }}>
                                    {renderKatifiMessage(msg.content)}
                                    {msg.image_base64 && (
                                        <img 
                                            src={msg.image_base64} 
                                            alt="Sent Attachment" 
                                            style={{ 
                                                maxWidth: '100%', 
                                                borderRadius: '8px', 
                                                marginTop: msg.content ? '10px' : '0', 
                                                display: 'block',
                                                cursor: 'zoom-in'
                                            }}
                                            onClick={() => setSelectedImage(msg.image_base64)}
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                        {katifiLoading && (
                            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                                <div style={{ padding: '12px 18px', borderRadius: '16px', borderTopLeftRadius: '4px', background: '#fff', border: '1px solid #e2e8f0', color: '#64748b' }}>
                                    Processing command...
                                </div>
                            </div>
                        )}
                        <div ref={katifiChatEndRef} />
                    </div>

                    {/* Attachment Preview */}
                    {attachmentBase64 && (
                        <div style={{ padding: '10px 20px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <div style={{ position: 'relative' }}>
                                <img src={attachmentBase64} alt="Attachment" style={{ height: '60px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                                <button type="button" onClick={() => setAttachmentBase64(null)} style={{ position: 'absolute', top: '-10px', right: '-10px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '50%', width: '20px', height: '20px', fontSize: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>X</button>
                            </div>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '500' }}>Image ready for Katifi Vision Analysis</span>
                        </div>
                    )}

                    {/* Input Area */}
                    <div style={{ padding: '20px', background: '#fff', borderTop: '1px solid #e2e8f0' }}>
                        <form onSubmit={handleKatifiSubmit} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                            <input type="file" accept="image/*" ref={hiddenFileInput} onChange={handleFileSelect} style={{ display: 'none' }} />
                            <button type="button" onClick={() => hiddenFileInput.current?.click()} disabled={katifiLoading || isCalling} title="Attach Photo for Analysis" style={{ background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: '50%', width: '50px', height: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', cursor: (katifiLoading || isCalling) ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}>
                                📎
                            </button>
                            <button 
                                type="button" 
                                onClick={toggleCall}
                                title={isCalling ? "End Voice Interface" : "Start Voice Interface"} 
                                style={{ 
                                    background: isCalling ? '#ef4444' : '#f1f5f9', 
                                    border: 'none', 
                                    borderRadius: '50%', 
                                    width: '50px', 
                                    height: '50px', 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    fontSize: '20px', 
                                    color: isCalling ? '#fff' : '#64748b', 
                                    cursor: 'pointer',
                                    boxShadow: isCalling ? '0 0 10px rgba(239, 68, 68, 0.5)' : 'none'
                                }}
                            >
                                {isCalling ? '📞' : '🎤'}
                            </button>
                            <input 
                                ref={katifiInputRef}
                                autoFocus
                                type="text"
                                value={katifiInput}
                                onChange={(e) => setKatifiInput(e.target.value)}
                                placeholder={isCalling ? "Voice engine active. Speak to Katifi..." : "Give a command or ask for a status report (e.g. 'What is the status of the Swarm?')..."}
                                style={{ flex: 1, padding: '15px', borderRadius: '25px', border: '1px solid #cbd5e1', fontSize: '15px', outline: 'none' }}
                                disabled={katifiLoading || isCalling}
                            />
                            <button type="submit" disabled={katifiLoading || isCalling || (!katifiInput.trim() && !attachmentBase64)} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: '25px', padding: '0 30px', fontWeight: 'bold', cursor: (katifiLoading || isCalling) ? 'not-allowed' : 'pointer', opacity: (katifiLoading || isCalling || (!katifiInput.trim() && !attachmentBase64)) ? 0.6 : 1 }}>
                                Send
                            </button>
                        </form>
                    </div>
                </div>
            ) : loading && activeTab === 'brain' ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>Loading Brain Interface...</div>
            ) : activeTab === 'brain' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '30px' }}>

                    {/* Main Editor */}
                    <div className="card" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                        <label className="form-label" style={{ display: 'block', marginBottom: '10px', fontWeight: '600' }}>
                            System Prompt: <span style={{ fontFamily: 'monospace', color: '#7c3aed' }}>{promptKey}</span>
                        </label>
                        <textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            spellCheck="false"
                            style={{
                                width: '100%',
                                height: '600px',
                                padding: '15px',
                                fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace',
                                fontSize: '14px',
                                lineHeight: '1.5',
                                borderRadius: '8px',
                                border: '1px solid #cbd5e1',
                                background: '#f8fafc',
                                color: '#334155',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    {/* Sidebar / Cheatsheet */}
                    <div>
                        <div className="card" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '15px', color: '#1e293b' }}>
                                Dynamic Variables
                            </h3>
                            <p style={{ fontSize: '13px', color: '#64748b', marginBottom: '15px' }}>
                                These placeholders are auto-replaced by the Engine during generation.
                            </p>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div style={{ padding: '10px', background: '#f1f5f9', borderRadius: '6px' }}>
                                    <code style={{ color: '#7c3aed', fontWeight: 'bold' }}>{"{{topic}}"}</code>
                                    <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>User's input topic</div>
                                </div>
                                <div style={{ padding: '10px', background: '#f1f5f9', borderRadius: '6px' }}>
                                    <code style={{ color: '#7c3aed', fontWeight: 'bold' }}>{"{{location_name}}"}</code>
                                    <div style={{ fontSize: '12px', color: '#475569', marginTop: '4px' }}>Target location (e.g. "Queens")</div>
                                </div>
                                <div style={{ padding: '10px', background: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                                    <code style={{ color: '#2563eb', fontWeight: 'bold' }}>{"{{seo_links_context}}"}</code>
                                    <div style={{ fontSize: '12px', color: '#1e40af', marginTop: '4px' }}>
                                        <strong>CRITICAL:</strong> The list of internal links from source of truth.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="card" style={{ background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px', color: '#1e293b' }}>
                                Prompt Metadata
                            </h3>
                            <div style={{ marginBottom: '10px' }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Description</label>
                                <input
                                    type="text"
                                    value={description || ''}
                                    onChange={(e) => setDescription(e.target.value)}
                                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', marginTop: '5px' }}
                                />
                            </div>
                        </div>
                    </div>

                </div>

            ) : activeTab === 'phoenix' ? (
                /* PHOENIX TAB */
                <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '30px', marginBottom: '40px' }}>
                        <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
                                <div style={{ fontSize: '48px', marginBottom: '10px' }}>🦅</div>
                                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>Operation Phoenix v3.0 ⚡</h2>
                                <p style={{ color: '#64748b', maxWidth: '500px', margin: '10px auto' }}>
                                    Revitalize legacy content with modern AI intelligence. Select a target period to fetch old posts, parse their links, and rewrite them as Cornerstone Guides.
                                    <br />
                                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#0f172a', background: '#f1f5f9', padding: '4px 8px', borderRadius: '4px', marginTop: '12px', display: 'inline-block' }}>
                                        Powered by Gemini 2.5 Flash & Deep Search
                                    </span>
                                </p>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '30px', maxWidth: '800px', margin: '0 auto 30px auto' }}>
                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Target Year</label>
                                    <select
                                        className="form-control"
                                        value={phoenixYear}
                                        onChange={(e) => setPhoenixYear(Number(e.target.value))}
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                    >
                                        {[...Array(11)].map((_, i) => (
                                            <option key={i} value={2015 + i}>{2015 + i}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Target Month</label>
                                    <select
                                        className="form-control"
                                        value={phoenixMonth}
                                        onChange={(e) => setPhoenixMonth(e.target.value ? Number(e.target.value) : '')}
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                    >
                                        <option value="">All Months</option>
                                        {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                                            <option key={i} value={i + 1}>{m}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Task Queue Size</label>
                                    <select
                                        className="form-control"
                                        value={phoenixLimit}
                                        onChange={(e) => setPhoenixLimit(Number(e.target.value))}
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                                    >
                                        <option value="1">1 Post (Test)</option>
                                        <option value="5">5 Posts</option>
                                        <option value="10">10 Posts</option>
                                        <option value="20">20 Posts</option>
                                    </select>
                                </div>
                            </div>

                            {/* [NEW] Speed Control Row */}
                            <div style={{ maxWidth: '800px', margin: '0 auto 30px auto' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>
                                    ⏱️ Processing Rate
                                </label>
                                <select
                                    className="form-control"
                                    value={phoenixDelay}
                                    onChange={(e) => setPhoenixDelay(Number(e.target.value))}
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc' }}
                                >
                                    <option value="0">🚀 Maximum Speed (Instant)</option>
                                    <option value="60">🐢 Safe Mode (1 post / hour)</option>
                                    <option value="144">📅 Daily Spread (10 posts / day)</option>
                                </select>
                                <p style={{ fontSize: '12px', color: '#64748b', marginTop: '5px' }}>
                                    Estimated Completion: {phoenixDelay > 0 ? `${(phoenixLimit * phoenixDelay) / 60} hours` : 'Immediate'}
                                </p>
                            </div>

                            <div style={{ textAlign: 'center' }}>
                                <button
                                    onClick={handlePhoenixTrigger}
                                    disabled={remastering}
                                    style={{
                                        padding: '16px 40px',
                                        borderRadius: '12px',
                                        border: 'none',
                                        background: remastering ? '#fcd34d' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        fontSize: '18px',
                                        cursor: remastering ? 'not-allowed' : 'pointer',
                                        boxShadow: '0 10px 15px -3px rgba(245, 158, 11, 0.3)',
                                        transition: 'transform 0.1s'
                                    }}
                                >
                                    {remastering ? 'Initializing Protocols...' : '🔥 Start Remastering Sequence'}
                                </button>
                                <p style={{ marginTop: '15px', fontSize: '13px', color: '#94a3b8' }}>
                                    ⚠️ Generated content will be saved as <strong>DRAFTS</strong> in WordPress.
                                </p>
                            </div>
                        </div>

                        {/* Log Console */}
                        <div className="card" style={{ background: '#1e293b', padding: '20px', borderRadius: '12px', color: '#fff', display: 'flex', flexDirection: 'column', maxHeight: '500px' }}>
                            <h3 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                Mission Log
                            </h3>
                            <div style={{ flex: 1, overflowY: 'auto', fontFamily: 'monospace', fontSize: '13px', lineHeight: '1.6' }}>
                                {phoenixLog.length === 0 ? (
                                    <span style={{ color: '#475569' }}>Waiting for command...</span>
                                ) : (
                                    phoenixLog.map((log, i) => (
                                        <div key={i} style={{ borderBottom: '1px solid #334155', paddingBottom: '4px', marginBottom: '4px' }}>
                                            {log}
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* [NEW] HISTORY TABLE */}
                    <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', color: '#1e293b' }}>🏆 Archive & Quality Control</h3>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '14px' }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Original Title</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>SEO</th>
                                    <th style={{ padding: '10px', textAlign: 'right' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(item => (
                                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px 10px', color: '#64748b' }}>
                                            {new Date(item.created_at).toLocaleDateString()}
                                        </td>
                                        <td style={{ padding: '12px 10px', fontWeight: '500' }}>
                                            {item.title}
                                        </td>
                                        <td style={{ padding: '12px 10px' }}>
                                            <span style={{
                                                padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold',
                                                background: item.status === 'success' ? '#dcfce7' : '#fee2e2',
                                                color: item.status === 'success' ? '#166534' : '#991b1b'
                                            }}>
                                                {item.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 10px', fontSize: '12px', color: '#64748b' }}>
                                            {item.seo_data?.focus_keyphrase || '-'}
                                        </td>
                                        <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => setSelectedDiff(item)}
                                                style={{
                                                    padding: '6px 12px', borderRadius: '6px', border: '1px solid #cbd5e1',
                                                    background: '#fff', cursor: 'pointer', fontSize: '13px'
                                                }}>
                                                👁️ Compare
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {history.length === 0 && (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
                                            No remastering history yet. Ignite Phoenix to begin.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* [NEW] DIFF MODAL */}
                    {selectedDiff && (
                        <div style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(0,0,0,0.85)', zIndex: 9999, padding: '40px',
                            display: 'flex', flexDirection: 'column'
                        }}>
                            <div style={{
                                background: '#fff', borderRadius: '12px', flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden'
                            }}>
                                {/* Modal Header */}
                                <div style={{ padding: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <h3 style={{ margin: 0, fontWeight: 'bold' }}>Diff View: {selectedDiff.title}</h3>
                                        <div style={{ fontSize: '13px', color: '#64748b', marginTop: '5px' }}>
                                            Focus Keyword: <strong>{selectedDiff.seo_data?.focus_keyphrase}</strong>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => setSelectedDiff(null)}
                                        style={{ fontSize: '24px', background: 'none', border: 'none', cursor: 'pointer' }}
                                    >
                                        &times;
                                    </button>
                                </div>

                                {/* Comparison Body */}
                                <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', overflow: 'hidden' }}>
                                    {/* OLD */}
                                    <div style={{ padding: '20px', borderRight: '1px solid #e2e8f0', overflowY: 'auto', background: '#ffe4e6' }}>
                                        <h4 style={{ marginBottom: '10px', color: '#be123c', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px' }}>Original (Legacy)</h4>
                                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedDiff.original_content_snapshot) }} style={{ fontSize: '14px', lineHeight: '1.6' }} />
                                    </div>

                                    {/* NEW */}
                                    <div style={{ padding: '20px', overflowY: 'auto', background: '#dcfce7' }}>
                                        <h4 style={{ marginBottom: '10px', color: '#15803d', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '1px' }}>Remastered (v2.0)</h4>
                                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedDiff.new_content_snapshot) }} style={{ fontSize: '14px', lineHeight: '1.6' }} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : activeTab === 'warfare' ? (
                /* WARFARE TAB (NEW) */
                <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                    {/* [NEW] Strategic Command Panel */}
                    <div style={{ marginBottom: '30px', paddingBottom: '30px', borderBottom: '1px solid #e2e8f0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>🦅 Strategic Command</h2>
                                <p style={{ color: '#64748b', marginTop: '5px' }}>
                                    Summon the Architect Agent to analyze recent victories and plan the next campaign.
                                </p>
                            </div>
                            <button
                                onClick={handleSummonArchitect}
                                disabled={analyzing}
                                style={{
                                    padding: '12px 24px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: analyzing ? '#94a3b8' : '#3b82f6',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    cursor: analyzing ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}
                            >
                                {analyzing ? 'Studying Battlefield...' : '🦅 Summon Architect Agent'}
                            </button>
                        </div>

                        {/* Proposed Plan View */}
                        {proposedPlan && (
                            <div style={{ marginTop: '30px', padding: '20px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                    <h3 style={{ margin: 0, color: '#0369a1' }}>📋 Proposed Campaign Plan ({proposedPlan.clusters?.length || 0} Clusters)</h3>
                                    <button
                                        onClick={handleExecutePlan}
                                        disabled={executingPlan}
                                        style={{
                                            padding: '10px 20px',
                                            borderRadius: '6px',
                                            background: '#16a34a',
                                            color: '#fff',
                                            border: 'none',
                                            fontWeight: 'bold',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {executingPlan ? 'Launching Campaign...' : '✅ Approve & Execute'}
                                    </button>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
                                    {(proposedPlan.clusters || []).map((cluster, idx) => (
                                        <div key={idx} style={{ background: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <div style={{ fontSize: '12px', color: '#64748b', textTransform: 'uppercase', marginBottom: '5px' }}>
                                                Topic Cluster
                                            </div>
                                            <h4 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#1e293b' }}>{cluster.topic_name}</h4>
                                            <div style={{ fontSize: '14px', fontWeight: '600', color: '#7c3aed', marginBottom: '10px' }}>
                                                "{cluster.cornerstone_title}"
                                            </div>
                                            <p style={{ fontSize: '13px', color: '#475569', fontStyle: 'italic', marginBottom: '10px' }}>
                                                {cluster.rationale}
                                            </p>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                                {cluster.target_keywords.slice(0, 3).map((k, i) => (
                                                    <span key={i} style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: '4px', fontSize: '11px', color: '#475569' }}>
                                                        {k}
                                                    </span>
                                                ))}
                                                {cluster.target_keywords.length > 3 && <span style={{ fontSize: '11px', color: '#94a3b8' }}>+{cluster.target_keywords.length - 3} more</span>}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>


                    {/* [NEW] MANUAL STRATEGY CONTROL */}
                    <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '30px' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', color: '#1e293b' }}>🎯 Manual Strategy Control</h3>

                        <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: '300px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>
                                    Target Keyword / Topic
                                </label>
                                <input
                                    type="text"
                                    value={manualKeyword}
                                    onChange={(e) => setManualKeyword(e.target.value)}
                                    placeholder="e.g. Estate Planning Lawyer Brooklyn"
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        fontSize: '16px'
                                    }}
                                    onKeyDown={(e) => e.key === 'Enter' && handleManualAdd()}
                                />
                            </div>

                            <div style={{ minWidth: '150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>
                                    Priority
                                </label>
                                <select
                                    value={manualPriority}
                                    onChange={(e) => setManualPriority(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        borderRadius: '8px',
                                        border: '1px solid #cbd5e1',
                                        background: '#f8fafc',
                                        fontSize: '16px'
                                    }}
                                >
                                    <option value="normal">Normal</option>
                                    <option value="high">High</option>
                                    <option value="urgent">Urgent</option>
                                </select>
                            </div>

                            <button
                                onClick={handleManualAdd}
                                disabled={addingManual || !manualKeyword.trim()}
                                style={{
                                    padding: '12px 30px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: !manualKeyword.trim() ? '#cbd5e1' : addingManual ? '#94a3b8' : '#10b981',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '16px',
                                    cursor: !manualKeyword.trim() || addingManual ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    height: '46px'
                                }}
                            >
                                {addingManual ? 'Queuing...' : '➕ Add to Queue'}
                            </button>
                        </div>
                    </div>

                    {/* Content Warfare Logs (Global) */}
                    <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>🛡️ Content Warfare Logs (Global)</h3>
                            <button
                                onClick={handleBatchExecute}
                                style={{
                                    padding: '10px 20px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)',
                                    color: '#fff',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 6px -1px rgba(124, 58, 237, 0.3)'
                                }}
                            >
                                🏭 Start Content Factory
                            </button>
                        </div>

                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '14px' }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Time</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Query / Keyword</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Type</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '10px', textAlign: 'right' }}>Priority</th>
                                    <th style={{ padding: '10px', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {warfareHistory.map(item => (
                                    <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '13px' }}>
                                            {new Date(item.created_at).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '12px 10px', fontWeight: '600', color: '#1e293b' }}>
                                            {item.query}
                                        </td>
                                        <td style={{ padding: '12px 10px' }}>
                                            <span style={{
                                                fontSize: '11px',
                                                padding: '4px 8px',
                                                borderRadius: '6px',
                                                background: '#f1f5f9',
                                                color: '#475569',
                                                border: '1px solid #e2e8f0'
                                            }}>
                                                {item.recipe?.mode || 'General'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 10px' }}>
                                            <span style={{
                                                padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold',
                                                background: item.status === 'published_monitoring' ? '#dcfce7' :
                                                    item.status === 'generating' ? '#dbeafe' :
                                                        item.status === 'failed' ? '#fee2e2' : '#f3f4f6',
                                                color: item.status === 'published_monitoring' ? '#166534' :
                                                    item.status === 'generating' ? '#1e40af' :
                                                        item.status === 'failed' ? '#991b1b' : '#374151'
                                            }}>
                                                {item.status.toUpperCase().replace('_', ' ')}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                                            <span style={{
                                                fontWeight: '600',
                                                color: item.priority === 'urgent' ? '#dc2626' : '#64748b'
                                            }}>
                                                {item.priority.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => setSelectedPlan(item)}
                                                style={{
                                                    background: 'none',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '6px',
                                                    padding: '4px 8px',
                                                    cursor: 'pointer',
                                                    color: '#475569',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                👁️ View
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {warfareHistory.length === 0 && (
                                    <tr>
                                        <td colSpan="6" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                            No active campaigns found in the system.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Plan Details Modal */}
                    {selectedPlan && (
                        <div style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <div style={{
                                background: '#fff', width: '600px', maxHeight: '80vh',
                                borderRadius: '12px', padding: '24px',
                                overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>📋 Strategic Plan Details</h3>
                                    <button
                                        onClick={() => setSelectedPlan(null)}
                                        style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}
                                    >
                                        ×
                                    </button>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Target Query</label>
                                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b' }}>{selectedPlan.query}</div>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '4px' }}>Status</label>
                                    <span style={{
                                        padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold',
                                        background: selectedPlan.status === 'published_monitoring' ? '#dcfce7' : '#f1f5f9',
                                        color: selectedPlan.status === 'published_monitoring' ? '#166534' : '#64748b'
                                    }}>
                                        {selectedPlan.status.toUpperCase()}
                                    </span>
                                </div>

                                {selectedPlan.published_url && (
                                    <div style={{ marginBottom: '16px', background: '#f0fdf4', padding: '12px', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                                        <label style={{ display: 'block', fontSize: '12px', color: '#166534', marginBottom: '4px' }}>✅ Published URL</label>
                                        <a href={selectedPlan.published_url} target="_blank" rel="noopener noreferrer" style={{ color: '#15803d', fontWeight: 'bold', textDecoration: 'underline' }}>
                                            {selectedPlan.published_url}
                                        </a>
                                    </div>
                                )}

                                <div>
                                    <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '8px' }}>Recipe / AI Config (JSON)</label>
                                    <pre style={{
                                        background: '#f8fafc', padding: '12px', borderRadius: '8px',
                                        fontSize: '12px', color: '#334155', overflowX: 'auto',
                                        border: '1px solid #e2e8f0'
                                    }}>
                                        {JSON.stringify(selectedPlan.recipe || {}, null, 2)}
                                    </pre>
                                </div>

                                <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => setSelectedPlan(null)}
                                        style={{
                                            padding: '8px 16px', borderRadius: '6px',
                                            background: '#f1f5f9', border: '1px solid #e2e8f0',
                                            cursor: 'pointer', fontWeight: '500', color: '#475569'
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ) : activeTab === 'dmx62' ? (
                /* --- TAB: DMX62 REALITY OVERRIDE --- */
                <div className="animate-fade-in">

                    {/* Header Card */}
                    <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    🔮 Semantic Reality Override (DMX62)
                                </h2>
                                <p style={{ color: '#64748b', fontSize: '14px', maxWidth: '600px', lineHeight: '1.5' }}>
                                    Target specific LLM hallucinations by injecting structured "FactCheck" data and flooding the semantic embedding space with corrective node clusters.
                                </p>
                            </div>
                            <div style={{ background: '#f8fafc', padding: '10px 20px', borderRadius: '8px', border: '1px solid #e2e8f0', textAlign: 'center' }}>
                                <div style={{ fontSize: '11px', fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase' }}>PROTOCOL STATUS</div>
                                <div style={{ color: '#10b981', fontWeight: 'bold', fontSize: '14px' }}>ACTIVE ●</div>
                            </div>
                        </div>
                    </div>

                    {/* Main Interface Grid */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>

                        {/* Control Panel */}
                        <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', margin: 0 }}>🎯 Target Vector Injection</h3>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setDmxKeyword("Morgan Legal Group Reviews Brooklyn");
                                        setDmxUrl("https://www.yelp.com/biz/morgan-martinez-legal-group-brooklyn");
                                    }}
                                    style={{
                                        padding: '5px 10px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        borderRadius: '6px',
                                        border: '1px solid #c4b5fd',
                                        background: '#f5f3ff',
                                        color: '#6d28d9',
                                        cursor: 'pointer'
                                    }}
                                >
                                    ⚡ Harvey Yelp Preset
                                </button>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>Target Hallucination / Keyword</label>
                                <input
                                    type="text"
                                    placeholder='e.g. "Smith Law Firm Bankruptcy Reviews"'
                                    value={dmxKeyword}
                                    onChange={(e) => setDmxKeyword(e.target.value)}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '8px',
                                        border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                                    }}
                                />
                            </div>

                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#334155', marginBottom: '8px' }}>False Source URL (Vector Node)</label>
                                <input
                                    type="text"
                                    placeholder='e.g. https://yellowpages.com/old-listing'
                                    value={dmxUrl}
                                    onChange={(e) => setDmxUrl(e.target.value)}
                                    style={{
                                        width: '100%', padding: '12px', borderRadius: '8px',
                                        border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none'
                                    }}
                                />
                            </div>

                            <button
                                onClick={handleDMXOverride}
                                disabled={dmxLoading}
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    borderRadius: '8px',
                                    border: 'none',
                                    background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                                    color: '#fff',
                                    fontSize: '15px',
                                    fontWeight: 'bold',
                                    cursor: dmxLoading ? 'not-allowed' : 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
                                }}
                            >
                                {dmxLoading ? 'INJECTING PAYLOAD...' : '🛡️ INIT REALITY OVERRIDE'}
                            </button>

                            <button
                                type="button"
                                onClick={handleHarveyYelpDossier}
                                disabled={dmxLoading || ollamaLoading}
                                style={{
                                    marginTop: '12px',
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #8b5cf6',
                                    background: '#faf5ff',
                                    color: '#6d28d9',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: (dmxLoading || ollamaLoading) ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                📋 GENERATE HARVEY YELP EVIDENCE DOSSIER
                            </button>

                            {/* Ollama Local AI Shield Status Box */}
                            <div style={{
                                marginTop: '16px', padding: '12px 14px', borderRadius: '8px',
                                background: ollamaStatus?.online ? '#f0fdf4' : '#f8fafc',
                                border: `1px solid ${ollamaStatus?.online ? '#bbf7d0' : '#e2e8f0'}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                            }}>
                                <div>
                                    <div style={{ fontSize: '11px', fontWeight: 'bold', color: ollamaStatus?.online ? '#15803d' : '#64748b' }}>
                                        {ollamaStatus?.online ? '🟢 OLLAMA LOCAL REASONING ENGINE' : '⚪ OLLAMA ENGINE (FALLBACK CLOUD)'}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#334155', fontWeight: '600' }}>
                                        {ollamaStatus?.online 
                                            ? `Model: ${ollamaStatus.preferred_reasoner || 'deepseek-r1:14b'} (0$ Cost)`
                                            : 'Local daemon offline — routing to Gemini'}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={fetchOllamaStatus}
                                    style={{
                                        fontSize: '11px', padding: '4px 8px', borderRadius: '4px',
                                        border: '1px solid #cbd5e1', background: '#fff', cursor: 'pointer'
                                    }}
                                >
                                    🔄 Refresh
                                </button>
                            </div>

                            <button
                                type="button"
                                onClick={handleRunOllamaAudit}
                                disabled={ollamaLoading || dmxLoading}
                                style={{
                                    marginTop: '10px',
                                    width: '100%',
                                    padding: '12px',
                                    borderRadius: '8px',
                                    border: '1px solid #059669',
                                    background: '#ecfdf5',
                                    color: '#065f46',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: (ollamaLoading || dmxLoading) ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                {ollamaLoading ? '🤖 DEEPSEEK-R1 AUDITING CITATION...' : '🤖 RUN OLLAMA DEEPSEEK-R1 AUDIT'}
                            </button>
                        </div>

                        {/* Status Console */}
                        <div className="card" style={{ background: '#1e293b', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', color: '#fff' }}>
                            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#e2e8f0', marginBottom: '20px', fontFamily: 'monospace' }}>🖥️ DMX62 TERMINAL OUTPUT</h3>

                            {!dmxStatus ? (
                                <div style={{ color: '#64748b', fontSize: '13px', fontFamily: 'monospace', lineHeight: '2' }}>
                                    [SYSTEM] Waiting for input...<br />
                                    [SYSTEM] Indexing API: READY<br />
                                    [SYSTEM] Semantic Flooding: READY<br />
                                    _
                                </div>
                            ) : dmxStatus.status === 'ollama_audit_ready' ? (
                                <div className="animate-fade-in" style={{ fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                        <span style={{ color: '#34d399', fontWeight: 'bold' }}>🧠 OLLAMA DEEPSEEK-R1 REPUTATION AUDIT</span>
                                        <span style={{ background: '#059669', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>
                                            {dmxStatus.duration_ms ? `${dmxStatus.duration_ms}ms (0$ COST)` : 'LOCAL GPU'}
                                        </span>
                                    </div>

                                    <div style={{ marginBottom: '12px', background: 'rgba(255,255,255,0.04)', padding: '10px', borderRadius: '6px' }}>
                                        <div style={{ color: '#f87171' }}>⚠️ Hallucination Risk: <b style={{ fontSize: '14px' }}>{dmxStatus.risk_score}% (HIGH)</b></div>
                                        <div style={{ color: '#fbbf24', fontSize: '11px', marginTop: '4px' }}>Conflict: {dmxStatus.conflict}</div>
                                        <div style={{ color: '#a78bfa', fontSize: '11px' }}>Action: {dmxStatus.recommended_action}</div>
                                    </div>

                                    {dmxStatus.chain_of_thought && (
                                        <div style={{ marginBottom: '12px' }}>
                                            <div style={{ color: '#a78bfa', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '4px' }}>
                                                💭 Chain-of-Thought (Deep Thinking)
                                            </div>
                                            <div style={{
                                                maxHeight: '110px', overflowY: 'auto', background: 'rgba(0,0,0,0.4)',
                                                padding: '8px', borderRadius: '6px', border: '1px solid rgba(167, 139, 250, 0.2)',
                                                whiteSpace: 'pre-wrap', color: '#94a3b8', fontSize: '10px', lineHeight: '1.4'
                                            }}>
                                                {dmxStatus.chain_of_thought}
                                            </div>
                                        </div>
                                    )}

                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ color: '#38bdf8', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '6px' }}>
                                            📋 Executive Audit & Findings
                                        </div>
                                        <div style={{
                                            maxHeight: '160px', overflowY: 'auto', background: 'rgba(0,0,0,0.35)',
                                            padding: '10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)',
                                            whiteSpace: 'pre-wrap', color: '#cbd5e1', fontSize: '11px', lineHeight: '1.5'
                                        }}>
                                            {dmxStatus.raw_analysis}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                                        <button
                                            type="button"
                                            onClick={handleDMXOverride}
                                            style={{
                                                flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #10b981',
                                                background: '#10b981', color: '#fff', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
                                            }}
                                        >
                                            ⚡ INJECT DMX62 OVERRIDE NOW
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleHarveyYelpDossier}
                                            style={{
                                                flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #8b5cf6',
                                                background: '#8b5cf6', color: '#fff', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
                                            }}
                                        >
                                            📋 GENERATE FULL DOSSIER
                                        </button>
                                    </div>
                                </div>
                            ) : dmxStatus.worker === 'Harvey Yelp' || dmxStatus.status === 'dossier_ready' ? (
                                <div className="animate-fade-in" style={{ fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                        <span style={{ color: '#a78bfa', fontWeight: 'bold' }}>🛡️ HARVEY YELP EVIDENTIARY DOSSIER</span>
                                        <span style={{ background: '#059669', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold' }}>GRADE {dmxStatus.evidence_grade || 'A+'}</span>
                                    </div>

                                    <div style={{ marginBottom: '12px', background: 'rgba(255,255,255,0.04)', padding: '10px', borderRadius: '6px' }}>
                                        <div style={{ color: '#f87171' }}>⚠️ Erroneous Listing: <span style={{ textDecoration: 'line-through' }}>{dmxStatus.erroneous_name}</span></div>
                                        <div style={{ color: '#4ade80' }}>✅ Canonical Entity: <b>{dmxStatus.canonical_name}</b></div>
                                        <div style={{ color: '#94a3b8', fontSize: '11px', marginTop: '4px' }}>Target: {dmxStatus.target_yelp_url}</div>
                                        <div style={{ color: '#38bdf8', fontSize: '11px' }}>Attorney: {dmxStatus.managing_attorney}</div>
                                    </div>

                                    <div style={{ marginBottom: '12px' }}>
                                        <div style={{ color: '#fbbf24', fontSize: '11px', fontWeight: 'bold', textTransform: 'uppercase', marginBottom: '6px' }}>🏛️ Official Verification Chain ({dmxStatus.evidence_items?.length || 6} Points)</div>
                                        <div style={{ maxHeight: '150px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            {(dmxStatus.evidence_items || []).map((item, idx) => (
                                                <div key={idx} style={{ background: 'rgba(255,255,255,0.03)', padding: '6px 8px', borderRadius: '4px', borderLeft: '3px solid #8b5cf6' }}>
                                                    <span style={{ color: '#c4b5fd', fontWeight: 'bold' }}>{item.category}:</span> <span style={{ color: '#e2e8f0' }}>{item.claim}</span>
                                                    <div style={{ color: '#94a3b8', fontSize: '10px' }}>Authority: {item.authority} (Weight: {item.evidentiary_weight})</div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const ticket = dmxStatus.appeal_ticket;
                                                const text = typeof ticket === 'string' ? ticket : ticket?.body || JSON.stringify(ticket, null, 2);
                                                navigator.clipboard.writeText(text);
                                                toast.success("📋 Official Yelp Appeal Notice copied!");
                                            }}
                                            style={{
                                                flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid #8b5cf6',
                                                background: '#8b5cf6', color: '#fff', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
                                            }}
                                        >
                                            📋 COPY YELP APPEAL LETTER
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                navigator.clipboard.writeText(JSON.stringify(dmxStatus.corrective_json_ld, null, 2));
                                                toast.success("📄 Schema.org CorrectionAnnotation copied!");
                                            }}
                                            style={{
                                                flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)',
                                                background: 'rgba(255,255,255,0.1)', color: '#e2e8f0', fontSize: '11px', fontWeight: 'bold', cursor: 'pointer'
                                            }}
                                        >
                                            📄 COPY SCHEMA JSON-LD
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="animate-fade-in" style={{ fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.6' }}>
                                    <div style={{ marginBottom: '16px' }}>
                                        <span style={{ color: '#4ade80' }}>[SUCCESS]</span> Payload Generated<br />
                                        <span style={{ color: '#94a3b8' }}>Target:</span> {dmxKeyword}<br />
                                        <span style={{ color: '#94a3b8' }}>Risk Score:</span> <span style={{ color: (dmxStatus?.metrics?.risk_score || 0) > 70 ? '#ef4444' : '#fbbf24', fontWeight: 'bold' }}>{dmxStatus?.metrics?.risk_score || 85}%</span>
                                    </div>

                                    <div style={{ marginBottom: '16px', padding: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.1)' }}>
                                        <div style={{ color: '#fbbf24', marginBottom: '4px', fontSize: '11px', textTransform: 'uppercase' }}>JSON-LD Injection</div>
                                        <div style={{ color: '#cbd5e1', wordBreak: 'break-all' }}>
                                            {JSON.stringify(dmxStatus?.json_ld || dmxStatus, null, 2).slice(0, 200)}...
                                        </div>
                                    </div>

                                    {(dmxStatus?.metrics?.risk_score || 0) > 70 && (
                                        <div style={{ padding: '10px', background: 'rgba(239, 68, 68, 0.2)', border: '1px solid #ef4444', borderRadius: '6px', color: '#fca5a5' }}>
                                            ⚠️ <b>SEMANTIC FLOODING TRIGGERED</b><br />
                                            {dmxStatus?.metrics?.flooding_status?.message || "Created 5 NUCLEAR tasks for semantic flooding."}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : activeTab === 'corrections' ? (
                <AICorrectionsTab activeTab={activeTab} />
            ) : activeTab === 'taskboard' ? (
                <AITaskBoardTab activeTab={activeTab} />
            ) : activeTab === 'memory' ? (
                <KatifiMemoryPanel activeTab={activeTab} />
            ) : activeTab === 'creator' ? (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Inner Step Navigation */}
                    <div style={{ display: 'flex', gap: '15px', marginBottom: '25px', paddingBottom: '15px', borderBottom: '1px solid #e2e8f0' }}>
                        <button 
                            onClick={() => setCreatorStep('architect')}
                            style={{ 
                                padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', 
                                background: creatorStep === 'architect' ? '#10b981' : '#f1f5f9', 
                                color: creatorStep === 'architect' ? '#fff' : '#64748b', 
                                fontWeight: 'bold', fontSize: '15px', transition: 'all 0.2s',
                                boxShadow: creatorStep === 'architect' ? '0 4px 6px -1px rgba(16, 185, 129, 0.2)' : 'none'
                            }}
                        >
                            Step 1: 🏗️ Site Architect (For New Sites)
                        </button>
                        <button 
                            onClick={() => setCreatorStep('web-studio')}
                            style={{ 
                                padding: '12px 24px', borderRadius: '8px', border: 'none', cursor: 'pointer', 
                                background: creatorStep === 'web-studio' ? '#3b82f6' : '#f1f5f9', 
                                color: creatorStep === 'web-studio' ? '#fff' : '#64748b', 
                                fontWeight: 'bold', fontSize: '15px', transition: 'all 0.2s',
                                boxShadow: creatorStep === 'web-studio' ? '0 4px 6px -1px rgba(59, 130, 246, 0.2)' : 'none'
                            }}
                        >
                            Step 2: 🎨 Katifi Web Studio (For Existing Sites)
                        </button>
                    </div>

                    {creatorStep === 'architect' ? (
                        <div style={{ display: 'flex', gap: '30px', height: '100%', minHeight: '800px' }}>
                            {/* LEFT PANEL: Form */}
                    <div className="card" style={{ flex: 1, background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', overflowY: 'auto' }}>
                        <div style={{ marginBottom: '30px' }}>
                            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b', marginBottom: '8px' }}>🏗️ Site Architect</h2>
                            <p style={{ color: '#64748b' }}>Configure your website layout, SEO optimization, and content elements, then launch Katifi-Design to build it.</p>
                        </div>

                        <form onSubmit={handleArchitectSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Target API/URL (Select Domain or Custom)</label>
                                <div>
                                    <input 
                                        type="text" 
                                        list="arch-domains-list"
                                        value={archUrl} 
                                        onChange={e => setArchUrl(e.target.value)} 
                                        placeholder="Or enter custom URL (e.g. https://probatenyattorney.com)" 
                                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} 
                                        required={!archUrl} 
                                    />
                                    <datalist id="arch-domains-list">
                                        {archDomains.map((d, index) => {
                                            const val = typeof d === 'string' ? d : (d.domain || d.url || '');
                                            return <option key={index} value={val}>{val}</option>;
                                        })}
                                    </datalist>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '20px' }}>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Location / City for Local SEO Focus</label>
                                    <input type="text" value={archCity} onChange={e => setArchCity(e.target.value)} placeholder="e.g. Brooklyn, NY" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Google Place ID (Crucial for Local SEO & Maps)</label>
                                    <input type="text" value={archPlaceId} onChange={e => setArchPlaceId(e.target.value)} placeholder="e.g. ChIJ5aflLgCr2YgR1D..." style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Pages to Generate / Redesign</label>
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                    {Object.keys(archPages).map(page => (
                                        <label key={page} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: '#f8fafc', padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <input type="checkbox" checked={archPages[page]} onChange={e => setArchPages({...archPages, [page]: e.target.checked})} style={{ width: '16px', height: '16px' }} />
                                            <span style={{ textTransform: 'capitalize', color: '#334155', fontWeight: '500' }}>{page}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Elements to PRESERVE / Force Inject</label>
                                <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                                    {Object.keys(archElements).map(el => (
                                        <label key={el} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', background: '#f0fdfa', padding: '8px 16px', borderRadius: '8px', border: '1px solid #ccfbf1' }}>
                                            <input type="checkbox" checked={archElements[el]} onChange={e => setArchElements({...archElements, [el]: e.target.checked})} style={{ width: '16px', height: '16px' }} />
                                            <span style={{ textTransform: 'capitalize', color: '#0f766e', fontWeight: 'bold' }}>{el === 'chat' ? 'Ngage Chat Widget' : el === 'calendar' ? 'Calendar Booking' : el}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ fontWeight: '600', color: '#475569', margin: 0 }}>Colors / Styling Guidelines</label>
                                    <button type="button" onClick={() => setArchColors("Modern luxury dark theme, onyx black #0f172a primary, bright gold #fbbf24 accents, elegant typography (Playfair Display + Inter).")} style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd', borderRadius: '6px', padding: '2px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.target.style.background = '#e0f2fe'} onMouseOut={e => e.target.style.background = '#f0f9ff'}>⚡ Auto</button>
                                </div>
                                <input type="text" value={archColors} onChange={e => setArchColors(e.target.value)} placeholder="e.g. Modern dark theme, Tailwind CSS gradients, #1e293b primary" style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }} />
                            </div>

                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                    <label style={{ fontWeight: '600', color: '#475569', margin: 0 }}>Core Content Prompt / Writing Strategy</label>
                                    <button type="button" onClick={() => setArchPrompt("Act as a top-tier luxury law firm. Focus on exclusive, high-end legal services. The tone should be authoritative, empathetic, and premium. Highlight our decades of experience, white-glove client service, and deep knowledge of New York law. Keep content highly professional but persuasive, optimized for conversion.")} style={{ background: '#f0f9ff', color: '#0284c7', border: '1px solid #bae6fd', borderRadius: '6px', padding: '2px 10px', fontSize: '12px', cursor: 'pointer', fontWeight: 'bold', transition: 'background 0.2s' }} onMouseOver={e => e.target.style.background = '#e0f2fe'} onMouseOut={e => e.target.style.background = '#f0f9ff'}>⚡ Auto</button>
                                </div>
                                <textarea value={archPrompt} onChange={e => setArchPrompt(e.target.value)} placeholder="Describe the mission of the firm, tone of voice, or specific unique selling propositions..." style={{ width: '100%', padding: '15px', borderRadius: '8px', border: '1px solid #cbd5e1', height: '150px', resize: 'vertical', fontFamily: 'inherit' }} required />
                            </div>

                            <button type="submit" disabled={archTerminalOpen} style={{ marginTop: '10px', padding: '16px', borderRadius: '12px', border: 'none', background: archTerminalOpen ? '#94a3b8' : '#0f766e', color: '#fff', fontSize: '18px', fontWeight: 'bold', cursor: archTerminalOpen ? 'not-allowed' : 'pointer', transition: 'background 0.2s', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                {archTerminalOpen ? '⚙️ Agent Dispatched...' : '🚀 Launch Katifi-Design Builder'}
                            </button>
                        </form>
                    </div>

                    {/* RIGHT PANEL: Hacker Terminal */}
                    <div style={{ width: '450px', background: '#0f172a', borderRadius: '12px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.5)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', background: '#1e293b', borderBottom: '1px solid #334155', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{ display: 'flex', gap: '6px' }}>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }}></div>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#f59e0b' }}></div>
                                <div style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#10b981' }}></div>
                            </div>
                            <span style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'monospace', marginLeft: '10px' }}>Katifi-Design live terminal</span>
                        </div>
                        <div style={{ flex: 1, padding: '20px', overflowY: 'auto', fontFamily: 'Menlo, Monaco, Consolas, "Courier New", monospace', fontSize: '12px', color: '#00f2fe', lineHeight: '1.6' }}>
                            {archLogs.length === 0 ? (
                                <div style={{ color: '#64748b' }}>Waiting for configuration... System standing by.</div>
                            ) : (
                                archLogs.map((log, i) => (
                                    <div key={i} style={{ marginBottom: '8px', wordBreak: 'break-word' }}>
                                        <span style={{ color: '#64748b', marginRight: '8px' }}>[{new Date().toLocaleTimeString()}]</span>
                                        <span style={{ color: '#38bdf8', fontWeight: 'bold', marginRight: '8px' }}>[{log.step}]</span>
                                        <span style={{ color: log.step === 'Error' ? '#ef4444' : '#e2e8f0' }}>{log.details}</span>
                                    </div>
                                ))
                            )}
                            {archTerminalOpen && archLogs.length > 0 && archLogs[archLogs.length - 1]?.step !== 'Complete' && archLogs[archLogs.length - 1]?.step !== 'Error' && (
                                <div style={{ display: 'inline-block', width: '8px', height: '15px', background: '#00f2fe', animation: 'blink 1s step-end infinite' }}></div>
                            )}
                            <style>{`
                                @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
                            `}</style>
                        </div>
                    </div>
                </div>
                    ) : creatorStep === 'web-studio' ? (
                        <KatifiWebStudioTab />
                    ) : null}
            </div>
            ) : (activeTab === 'videoarchive' && isRealCreator) ? (
                <KatifiVideoArchive />
            ) : activeTab === 'cleanup' ? (
            /* SITE CLEANUP TAB */
            <div className="tab-pane active" style={{ animation: 'fadeIn 0.3s ease-out', height: '100%' }}>
                <div className="card" style={{ background: '#fff', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxWidth: '800px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '10px' }}>🧹</div>
                        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1e293b' }}>Global Site Cleanup</h2>
                        <p style={{ color: '#64748b', marginTop: '10px' }}>
                            Instantly scan all connected WordPress domains and permanently delete any pages or posts mentioning a specific lawyer.
                        </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>First Name</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. John"
                                value={cleanupFirstName}
                                onChange={(e) => setCleanupFirstName(e.target.value)}
                                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                        <div>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#475569' }}>Last Name</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="e.g. Doe"
                                value={cleanupLastName}
                                onChange={(e) => setCleanupLastName(e.target.value)}
                                style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
                            />
                        </div>
                    </div>

                    <div style={{ textAlign: 'center' }}>
                        <button
                            onClick={handleSiteCleanup}
                            disabled={isCleaning}
                            style={{
                                padding: '16px 40px',
                                borderRadius: '12px',
                                border: 'none',
                                background: isCleaning ? '#fca5a5' : '#ef4444',
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: '18px',
                                cursor: isCleaning ? 'not-allowed' : 'pointer',
                                boxShadow: '0 10px 15px -3px rgba(239, 68, 68, 0.3)',
                                transition: 'background 0.2s'
                            }}
                        >
                            {isCleaning ? 'Purging in progress...' : '🗑️ Scan and Remove Global Content'}
                        </button>
                        <p style={{ marginTop: '15px', fontSize: '12px', color: '#dc2626', fontWeight: 'bold' }}>
                            ⚠️ WARNING: This action searches ALL connected sites and forcibly deletes matching pages. It cannot be undone.
                        </p>
                    </div>

                    {/* [NEW] SITE CLEANUP LOGS TABLE */}
                    <div style={{ marginTop: '40px', borderTop: '2px solid #e2e8f0', paddingTop: '30px' }}>
                        <h3 style={{ fontSize: '20px', fontWeight: 'bold', marginBottom: '20px', color: '#1e293b' }}>
                            📋 Cleanup History & Reports
                        </h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b', fontSize: '14px' }}>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Date</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Target Name</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Scanned Domains</th>
                                    <th style={{ padding: '10px', textAlign: 'left' }}>Status</th>
                                    <th style={{ padding: '10px', textAlign: 'right' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {cleanupLogs.map(log => (
                                    <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '12px 10px', color: '#64748b', fontSize: '13px' }}>
                                            {new Date(log.created_at).toLocaleString()}
                                        </td>
                                        <td style={{ padding: '12px 10px', fontWeight: '600', color: '#1e293b' }}>
                                            {log.search_query}
                                        </td>
                                        <td style={{ padding: '12px 10px', color: '#64748b' }}>
                                            {log.domains_scanned}
                                        </td>
                                        <td style={{ padding: '12px 10px' }}>
                                            <span style={{
                                                padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold',
                                                background: log.status === 'completed' ? '#dcfce7' :
                                                    log.status === 'running' ? '#dbeafe' : '#fee2e2',
                                                color: log.status === 'completed' ? '#166534' :
                                                    log.status === 'running' ? '#1e40af' : '#991b1b'
                                            }}>
                                                {log.status.toUpperCase()}
                                            </span>
                                        </td>
                                        <td style={{ padding: '12px 10px', textAlign: 'right' }}>
                                            <button
                                                onClick={() => setSelectedCleanupLog(log)}
                                                style={{
                                                    background: 'none',
                                                    border: '1px solid #cbd5e1',
                                                    borderRadius: '6px',
                                                    padding: '4px 8px',
                                                    cursor: 'pointer',
                                                    color: '#475569',
                                                    fontSize: '12px'
                                                }}
                                            >
                                                👁️ View Details ({log.deleted_items?.length || 0})
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                                {cleanupLogs.length === 0 && (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
                                            No cleanup tasks recorded yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Modal for Details */}
                    {selectedCleanupLog && (
                        <div style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(0,0,0,0.5)', zIndex: 1000,
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            <div style={{
                                background: '#fff', width: '700px', maxHeight: '80vh',
                                borderRadius: '12px', padding: '24px',
                                overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                                    <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>
                                        🗑️ Report: {selectedCleanupLog.search_query}
                                    </h3>
                                    <button
                                        onClick={() => setSelectedCleanupLog(null)}
                                        style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer' }}
                                    >
                                        ×
                                    </button>
                                </div>
                                <div style={{ marginBottom: '15px' }}>
                                    <strong>Domains Scanned:</strong> {selectedCleanupLog.domains_scanned}<br/>
                                    <strong>Status:</strong> {selectedCleanupLog.status}<br/>
                                    <strong>Date:</strong> {new Date(selectedCleanupLog.created_at).toLocaleString()}
                                </div>

                                <h4 style={{ fontSize: '14px', borderBottom: '1px solid #e2e8f0', paddingBottom: '8px', marginBottom: '15px' }}>
                                    Deleted Items ({selectedCleanupLog.deleted_items?.length || 0})
                                </h4>
                                
                                {(!selectedCleanupLog.deleted_items || selectedCleanupLog.deleted_items.length === 0) ? (
                                    <div style={{ padding: '20px', background: '#f8fafc', borderRadius: '8px', textAlign: 'center', color: '#64748b' }}>
                                        No items were deleted during this cleanup.
                                    </div>
                                ) : (
                                    <ul style={{ listStyleType: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {selectedCleanupLog.deleted_items.map((item, idx) => (
                                            <li key={idx} style={{ padding: '12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px' }}>
                                                <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 'bold', marginBottom: '4px', textTransform: 'uppercase' }}>
                                                    {item.type} (ID: {item.post_id})
                                                </div>
                                                <div style={{ fontSize: '15px', fontWeight: '600', color: '#1e293b', marginBottom: '2px' }}>
                                                    {item.title}
                                                </div>
                                                <div style={{ fontSize: '13px', color: '#64748b' }}>
                                                    Domain: <span style={{ color: '#0f766e', textDecoration: 'underline' }}>{item.domain_url}</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end' }}>
                                    <button
                                        onClick={() => setSelectedCleanupLog(null)}
                                        style={{
                                            padding: '8px 16px', borderRadius: '6px',
                                            background: '#f1f5f9', border: '1px solid #e2e8f0',
                                            cursor: 'pointer', fontWeight: '500', color: '#475569'
                                        }}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        ) : null}
        {lightboxPortal}
        </div>
    );
};

export default AdminBrainCenter;
